import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  createMessage,
  replyToMessage,
  fetchAssistantResponse,
} from '@/api/messages';

interface SendMessageState {
  /** Whether a request is currently in flight */
  isLoading: boolean;
  /** Error message if the request failed */
  error: string | null;
  /** Message ID, set when the response is received */
  messageId: string | null;
  /** Message title, set when the response is received */
  title: string | null;
}

const INITIAL_STATE: SendMessageState = {
  isLoading: false,
  error: null,
  messageId: null,
  title: null,
};

/**
 * Custom hook that sends a message via the two-step API pattern:
 *   1. POST /messages (or /messages/{id}/reply) to save the user's message
 *      and get a stream_url.
 *   2. GET the stream_url to fetch the assistant's complete response.
 *
 * Exposes loading, error, and result state.
 */
export function useSendMessage() {
  const [state, setState] = useState<SendMessageState>(INITIAL_STATE);
  const queryClient = useQueryClient();

  const sendMessage = useCallback(
    async (content: string, messageId?: string) => {
      // Reset state for new request
      setState({
        isLoading: true,
        error: null,
        messageId: null,
        title: null,
      });

      try {
        // Step 1: Create message or reply (via API Gateway)
        let streamUrl: string;

        if (messageId) {
          const reply = await replyToMessage(messageId, content);
          streamUrl = reply.stream_url;
        } else {
          const created = await createMessage(content);
          streamUrl = created.stream_url;
        }

        // Step 2: Fetch the assistant's response (via Lambda Function URL)
        const assistantResponse = await fetchAssistantResponse(streamUrl);

        setState({
          isLoading: false,
          error: null,
          messageId: assistantResponse.message_id,
          title: assistantResponse.title,
        });

        // Invalidate message queries so the list and detail views update
        queryClient.invalidateQueries({ queryKey: ['messages'] });
      } catch (err) {
        setState({
          isLoading: false,
          error: err instanceof Error ? err.message : 'An error occurred',
          messageId: null,
          title: null,
        });
      }
    },
    [queryClient],
  );

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    ...state,
    sendMessage,
    reset,
  };
}
